import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    connectAccount,
    getCountrySpecs,
} from '../../redux/businessAccount/detailsAction';
import { toast } from 'react-toastify';
import { submitBusinessDetails } from '../../redux/authentication/actionCreator';
import { numericRegex, options } from '../../helpers/constants';

const ukPostcodeRegex = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;
const nameRegex = /^[a-zA-Z\s'-]+$/;
const allowedDocTypes = [
    'image/jpeg',
    'image/png',
    'image/jpg',
    'application/pdf',
];

const excludedFields = [
    'business_profile.mcc',
    'business_profile.url',
    'business_type',
    'individual.dob.day',
    'individual.dob.month',
    'individual.dob.year',
    'tos_acceptance.ip',
    'tos_acceptance.date',
    'external_account',
    'individual.phone',
];

const getFieldType = (field) => {
    field = field.split('.').at(-1);
    switch (field) {
        case 'city':
        case 'line1':
        case 'postal_code':
        case 'state':
        case 'first_name':
        case 'last_name':
        case 'phone':
            return 'text';
        case 'email':
            return 'email';
        case 'date':
            return 'date';
        default:
            return 'text';
    }
};

const formatFieldName = (field) => {
    const cleanField = field.replace(
        /^(individual\.|business_profile\.|dob\.|address\.)+/,
        ''
    );
    return cleanField.replace(/\./g, ' ').replace(/_/g, ' ');
};

const formatSortCode = (value) => {
    const digitsOnly = value.replace(/\D/g, '');

    return digitsOnly
        .replace(/(\d{2})(\d{2})(\d{0,2})/, '$1-$2-$3')
        .trim()
        .replace(/-$/, '');
};

const getFieldLabel = (field) => {
    const label = formatFieldName(field);
    return label === 'line1' ? 'Address Line 1' : label;
};

const getFieldPlaceholder = (field) => {
    const cleanName = field.split('.').at(-1);

    switch (cleanName) {
        case 'first_name':
            return 'Enter your first name';
        case 'last_name':
            return 'Enter your last name';
        case 'city':
            return 'Enter your city';
        case 'line1':
            return 'Enter your street address';
        case 'postal_code':
            return 'e.g. SW1A 1AA';
        case 'email':
            return 'name@example.com';
        case 'state':
            return 'Enter your county';
        default:
            return `Enter your ${getFieldLabel(field).toLowerCase()}`;
    }
};

const getNestedValue = (obj, path) => {
    if (!obj) return undefined;
    const value = path.split('.').reduce((acc, part) => acc && acc[part] !== undefined ? acc[part] : undefined, obj);
    if (value !== undefined) return value;
    return obj[path];
};

const getVisibleFields = (countrySpecs) =>
    countrySpecs
        ?.filter((field) => !excludedFields.includes(field))
        .sort((a, b) => {
            const priorityFields = [
                'individual.first_name',
                'individual.last_name',
            ];
            if (priorityFields.includes(a) && !priorityFields.includes(b)) {
                return -1;
            }
            if (!priorityFields.includes(a) && priorityFields.includes(b)) {
                return 1;
            }
            return 0;
        }) || [];

const getInitialValues = (dynamicFields, tempData, formattedDate) => {
    const values = {
        business_type: 'individual',
        mcc: '5045',
        url: 'https://bestcookieco.com',
        ip: '172.18.80.19',
        type: 'custom',
        userId: tempData?.user_id || '',
        country: 'GB',
        currency: 'gbp',
        date: formattedDate,
        day: '',
        month: '',
        year: '',
        phone: '',
        ibn_number: '',
        routing_number: '',
        dob: '',
        additional_document: null,
    };

    dynamicFields.forEach((field) => {
        const parts = field.split('.');
        let current = values;
        for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) {
                current[parts[i]] = {};
            }
            current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = '';
    });

    return values;
};

const buildValidationSchema = (dynamicFields) => {
    const shape = {};

    dynamicFields.forEach((field) => {
        const cleanName = field.split('.').at(-1);
        const label = getFieldLabel(field);
        let rules;

        switch (cleanName) {
            case 'first_name':
            case 'last_name':
                rules = Yup.string()
                    .trim()
                    .required(`${label} is required`)
                    .matches(
                        nameRegex,
                        `${label} should only contain letters`
                    );
                break;
            case 'email':
                rules = Yup.string()
                    .trim()
                    .required('Email is required')
                    .email('Email must be a valid email address');
                break;
            case 'postal_code':
                rules = Yup.string()
                    .trim()
                    .required(`${label} is required`)
                    .matches(
                        ukPostcodeRegex,
                        'Please enter a valid UK postcode'
                    );
                break;
            default:
                rules = Yup.string()
                    .trim()
                    .required(`${label} is required`);
        }

        const parts = field.split('.');
        let current = shape;
        for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) {
                current[parts[i]] = {};
            }
            current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = rules;
    });

    const convertToYup = (obj) => {
        const yupShape = {};
        Object.entries(obj).forEach(([key, val]) => {
            if (val && typeof val === 'object' && val.constructor === Object) {
                yupShape[key] = Yup.object().shape(convertToYup(val));
            } else {
                yupShape[key] = val;
            }
        });
        return yupShape;
    };

    const nestedShape = convertToYup(shape);

    nestedShape.phone = Yup.string()
        .required('Phone number is required')
        .matches(/^\d{10,}$/, 'Phone number must be at least 10 digits');

    nestedShape.ibn_number = Yup.string()
        .required('Account number is required')
        .matches(/^\d{8}$/, 'Account number must be 8 digits');

    nestedShape.routing_number = Yup.string()
        .required('Sort code is required')
        .test(
            'sort-code',
            'Sort code must be 6 digits (e.g., 10-88-00)',
            (value) => Boolean(value && value.replace(/\D/g, '').length === 6)
        );

    nestedShape.dob = Yup.string()
        .required('Date of birth is required')
        .test('age', 'You must be at least 18 years old', (value) => {
            if (!value) return false;
            const minAge = new Date();
            minAge.setFullYear(minAge.getFullYear() - 18);
            return new Date(value) <= minAge;
        });

    nestedShape.additional_document = Yup.mixed()
        .required('Identity document is required')
        .test(
            'fileType',
            'Please upload a JPG, PNG, or PDF file',
            (value) => Boolean(value && allowedDocTypes.includes(value.type))
        );

    return Yup.object().shape(nestedShape);
};

const RequiredLabel = ({ children, className = '' }) => (
    <label
        className={`form-label c-667085 poppins f-14 fw-400 lh-20 ${className}`}
    >
        {children} <span className="text-danger">*</span>
    </label>
);

const ConnectAccount = ({ formData }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { loading } = useSelector((state) => state.auth);
    const { tempData } = useSelector((state) => state.user);
    const { countrySpecs, isLoading } = useSelector(
        (state) => state.businessDetails
    );
    const formattedDate = new Intl.DateTimeFormat('en-CA').format(new Date());

    const [countryCode, setCountryCode] = useState('+44');
    const [searchTerm, setSearchTerm] = useState('');

    const visibleFields = useMemo(
        () => getVisibleFields(countrySpecs),
        [countrySpecs]
    );

    const validationSchema = useMemo(
        () => buildValidationSchema(visibleFields),
        [visibleFields]
    );

    const initialValues = useMemo(
        () => getInitialValues(visibleFields, tempData, formattedDate),
        [visibleFields, tempData, formattedDate]
    );

    const filteredOptions = options.filter((country) =>
        country.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const submitBusinessForm = () => {
        const payload = new FormData();
        payload.append('name', formData.name);
        payload.append('website', formData.website);
        payload.append('latitude', formData.latitude);
        payload.append('longitude', formData.longitude);
        payload.append('address', formData.address);
        payload.append('user_id', tempData.user_id);

        if (formData.length === 0) {
            toast.error(
                'Please upload at least one image related to your business.'
            );
            return;
        }

        formData?.images.forEach((image) => {
            payload.append('images', image);
        });
        formData?.services.forEach((item, index) => {
            payload.append(`categories[${index}]`, item);
        });

        dispatch(submitBusinessDetails(payload, history, tempData, () => {}))
            .catch((err) => {
                console.error('Failed to submit business details:', err);
            });
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
        onSubmit: (values) => {
            const formDataObj = new FormData();

            Object.entries(values).forEach(([key, value]) => {
                if (['phone', 'routing_number', 'dob'].includes(key)) {
                    return;
                }
                if (typeof value === 'object' && value !== null && key !== 'additional_document') {
                    return;
                }
                if (visibleFields.includes(key)) {
                    return;
                }
                if (value === null || value === '') {
                    return;
                }

                const cleanKey = key.replace(
                    /^(individual\.|business_profile\.|individual.dob\.|address\.|tos_acceptance\.)+/,
                    ''
                );

                formDataObj.append(cleanKey, value);
            });

            visibleFields.forEach((field) => {
                const value = getNestedValue(values, field);
                if (value !== undefined && value !== null && value !== '') {
                    const cleanKey = field.replace(
                        /^(individual\.|business_profile\.|individual.dob\.|address\.|tos_acceptance\.)+/,
                        ''
                    );
                    formDataObj.append(cleanKey, value);
                }
            });

            formDataObj.append('phone', countryCode + values.phone);
            formDataObj.append('routing_number', values.routing_number);

            dispatch(
                connectAccount(formDataObj, (response) => {
                    if (response?.id) {
                        submitBusinessForm();
                    } else {
                        console.error('Error: No response ID found');
                    }
                })
            ).catch((err) => {
                console.error('Failed to connect account:', err);
            });
        },
    });

    useEffect(() => {
        dispatch(getCountrySpecs('GB'));
    }, [dispatch]);

    const handleCountryCodeClick = (_country) => {
        setCountryCode(_country.code);
    };

    const showError = (field) => {
        const touched = getNestedValue(formik.touched, field);
        const error = getNestedValue(formik.errors, field);
        return touched && error ? (
            <p className="text-danger mt-1 mb-0 ms-1" style={{ fontSize: '12px', textAlign: 'left' }}>
                {error}
            </p>
        ) : null;
    };

    const maxDobDate = new Date(
        new Date().setFullYear(new Date().getFullYear() - 18)
    )
        .toISOString()
        .split('T')[0];

    return (
        <div className="container mt-5 mx-auto" style={{ maxWidth: '600px' }}>
            <h2 className="text-center mb-4">Connect Payment Account</h2>
            <form
                onSubmit={formik.handleSubmit}
                className="border p-4 rounded shadow-sm"
                noValidate
            >
                {visibleFields.map((field) => (
                    <div className="mb-3 position-relative" key={field}>
                        <RequiredLabel className="text-capitalize">
                            {getFieldLabel(field)}
                        </RequiredLabel>
                        <input
                            type={getFieldType(field)}
                            className="form-control input-filed"
                            name={field}
                            value={getNestedValue(formik.values, field) || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder={getFieldPlaceholder(field)}
                            {...(getFieldType(field) === 'date'
                                ? {
                                      max: new Date()
                                          .toISOString()
                                          .split('T')[0],
                                  }
                                : {})}
                        />
                        {showError(field)}
                    </div>
                ))}

                <div className="mb-3 position-relative">
                    <RequiredLabel>Phone Number</RequiredLabel>
                    <div className="input-group input-filed mb-20">
                        <span
                            className="input-group-text border-0 py-0 ps-0 bg-transparent"
                            id="basic-addon1"
                        >
                            <div className="dropdown">
                                <button
                                    className="btn btn-country-num dropdown-toggle"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    <div className="container-fluid">
                                        <div className="row">
                                            <div className="col px-0">
                                                {countryCode}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                                <div className="dropdown-menu country-dropdown-menu">
                                    <input
                                        className="country-dropdown-input"
                                        type="text"
                                        placeholder="Search for a country..."
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                    />
                                    {filteredOptions.map((_country) => (
                                        <p
                                            key={_country.label}
                                            className="dropdown-item cp"
                                            onClick={() =>
                                                handleCountryCodeClick(_country)
                                            }
                                        >
                                            {_country.flag} {_country.label}{' '}
                                            {_country.code}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </span>
                        <input
                            name="phone"
                            value={formik.values.phone}
                            type="text"
                            onChange={(e) => {
                                if (numericRegex.test(e.target.value)) {
                                    formik.setFieldValue(
                                        'phone',
                                        e.target.value
                                    );
                                }
                            }}
                            onBlur={formik.handleBlur}
                            className="form-control border-0 py-0 box-shadow-0"
                            placeholder="Enter your phone number"
                            aria-label="mobileNumber"
                            aria-describedby="basic-addon1"
                        />
                    </div>
                    {showError('phone')}
                </div>

                <div className="mb-3 position-relative">
                    <RequiredLabel>Account Number</RequiredLabel>
                    <input
                        type="text"
                        className="form-control input-filed"
                        name="ibn_number"
                        value={formik.values.ibn_number}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="e.g. 00012345"
                    />
                    {showError('ibn_number')}
                </div>

                <div className="mb-3 position-relative">
                    <RequiredLabel>Sort Code</RequiredLabel>
                    <input
                        type="text"
                        className="form-control input-filed"
                        name="routing_number"
                        value={formik.values.routing_number}
                        onChange={(e) => {
                            formik.setFieldValue(
                                'routing_number',
                                formatSortCode(e.target.value)
                            );
                        }}
                        onBlur={formik.handleBlur}
                        maxLength={8}
                        placeholder="e.g. 10-88-00"
                    />
                    {showError('routing_number')}
                </div>

                <div className="mb-3 position-relative">
                    <RequiredLabel>Date of Birth</RequiredLabel>
                    <input
                        type="date"
                        className="form-control input-filed"
                        name="dob"
                        value={formik.values.dob}
                        onChange={(e) => {
                            const value = e.target.value;
                            const [year, month, day] = value.split('-');
                            formik.setFieldValue('dob', value);
                            formik.setFieldValue('day', day || '');
                            formik.setFieldValue('month', month || '');
                            formik.setFieldValue('year', year || '');
                        }}
                        onBlur={formik.handleBlur}
                        max={maxDobDate}
                    />
                    {showError('dob')}
                </div>

                <div className="mb-3 position-relative">
                    <RequiredLabel>Identity Document</RequiredLabel>
                    <input
                        type="file"
                        className="form-control input-filed"
                        name="additional_document"
                        onChange={(e) => {
                            formik.setFieldValue(
                                'additional_document',
                                e.currentTarget.files[0] || null
                            );
                        }}
                        onBlur={formik.handleBlur}
                    />
                    {showError('additional_document')}
                </div>

                <button
                    type="submit"
                    className="btn btn-next w-100"
                    disabled={isLoading || loading}
                >
                    {isLoading || loading ? 'Submitting...' : 'Submit'}
                </button>
            </form>
        </div>
    );
};

export default ConnectAccount;
